import React, { FC, useCallback, useEffect, useMemo } from "react";
import Loader from "@components/Loader/Loader";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import {
  useCreateCompanionMutation,
  useGetCompanionsQuery,
  useUpdateCompanionMutation,
} from "@store/companionApi";
import { useGetAvatarsQuery, useGetVoicesQuery } from "@store/avatarApi";
import HomeFormButtons from "@components/Home/HomeFormButtons/HomeFormButtons";
import TextIconButton from "@components/TextIconButton";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import HomeForm from "@components/Home/HomeForm/HomeForm";
import CompanionForm from "@components/CompanionForm";
import { CompanionFormType } from "@components/CompanionForm/CompanionForm";
import { Companion as CompanionModel } from "@models/companion";
import { setMessage } from "@store/snackbarSlice";
import { useGetAccountsQuery } from "@store/accountApi";
import { useUpdatePatientSettingsMutation } from "@store/patientApi";
import { mergePatientSettings } from "@models/patient-settings";
import { setSelectedPatient } from "@store/homeSlice";

const Companion: FC<{ isPatient: boolean }> = ({ isPatient = true }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state: RootState) => state.auth.user?.id);
  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const patient = useSelector((state: RootState) => state.home.selectedPatient);
  const { data: accounts } = useGetAccountsQuery(undefined, {
    skip: isPatient,
  });
  const { data: companions } = useGetCompanionsQuery(
    {
      patientId: (isPatient ? patientId : undefined) ?? undefined,
      userId: userId!,
    },
    {
      skip: !patientId && !userId,
    },
  );
  const { data: avatars } = useGetAvatarsQuery();
  const { data: voices } = useGetVoicesQuery();
  const [updateCompanionApi] = useUpdateCompanionMutation();
  const [createCompanionApi] = useCreateCompanionMutation();
  const [updatePatientSettings] = useUpdatePatientSettingsMutation();

  const language = useMemo(() => {
    if (isPatient) {
      return patient ? patient.language : null;
    }
    const account = accounts?.data?.[0];
    return account ? account.language : null;
  }, [isPatient, patient, accounts]);

  const { t } = useTranslation();

  const form = useForm<CompanionFormType>();
  const {
    clearErrors,
    reset,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = form;

  const resetForm = useCallback(
    (comp?: CompanionModel) => {
      reset({
        name: comp?.name ?? "",
        avatarId: comp?.avatar?.id ?? undefined,
        voiceId: comp?.voice?.id ?? undefined,
        isDefault: comp?.isDefault ?? true,
        ...(isPatient
          ? { openWithDateLocation: patient?.openWithDateLocation !== false }
          : {}),
      });
    },
    [reset, isPatient, patient?.openWithDateLocation],
  );

  useEffect(() => {
    resetForm(companions?.data?.[0]);
  }, [resetForm, companions]);

  const createCompanion = useCallback(
    async (companion: Partial<CompanionModel>) => {
      const res = await createCompanionApi(companion);
      if (res.error) {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      } else {
        dispatch(
          setMessage({
            content: t(`companions.drawer.saveSuccessMessage`),
            severity: "success",
          }),
        );
      }
      return res;
    },
    [dispatch, t, createCompanionApi],
  );

  const updateCompanion = useCallback(
    async (companion: Partial<CompanionModel> & { id: string }) => {
      const res = await updateCompanionApi(companion);
      if (res.error) {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      } else {
        dispatch(
          setMessage({
            content: t(`companions.drawer.saveSuccessMessage`),
            severity: "success",
          }),
        );
      }
      return res;
    },
    [dispatch, t, updateCompanionApi],
  );

  const onSave = useCallback(
    async (
      companion: Omit<
        CompanionModel,
        "id" | "patientId" | "avatar" | "voice" | "isDefault"
      >,
    ) => {
      companions?.data?.[0]?.id
        ? await updateCompanion({
            ...companion,
            id: companions?.data?.[0].id!,
          })
        : await createCompanion({
            ...companion,
          });
    },
    [companions, createCompanion, updateCompanion],
  );

  const onSubmit = async (formValue: CompanionFormType) => {
    if (!isValid) {
      return;
    }

    const companion = {
      name: formValue.name!,
      avatarId: formValue.avatarId!,
      voiceId: formValue.voiceId!,
      patientId: isPatient ? patientId : undefined,
      userId: !isPatient ? userId : undefined,
    };
    await onSave(companion);

    if (isPatient && patientId && patient) {
      try {
        const updatedSettings = await updatePatientSettings({
          patientId,
          openWithDateLocation: formValue.openWithDateLocation ?? true,
        }).unwrap();
        dispatch(
          setSelectedPatient(mergePatientSettings(patient, updatedSettings)),
        );
      } catch {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      }
    }
  };

  const loading = !avatars?.data?.length || !language;

  return (
    <Box display="flex" flexDirection="column">
      {loading ? (
        <Loader />
      ) : (
        <form
          onSubmit={(e) => {
            clearErrors();
            return handleSubmit(onSubmit)(e);
          }}
        >
          <HomeForm>
            <CompanionForm
              form={form}
              avatars={avatars?.data}
              voices={voices?.data}
              language={language}
              showConversationSettings={isPatient}
            />
          </HomeForm>

          <HomeFormButtons>
            <TextIconButton
              id="save-button"
              type="submit"
              disabled={isSubmitting}
              variant="contained"
              size="large"
              text={t(`companions.drawer.saveButton`)}
              icon={SaveOutlinedIcon}
            />
          </HomeFormButtons>
        </form>
      )}
    </Box>
  );
};

export default Companion;
